import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Grid, 
  FormControlLabel, 
  Switch, 
  CircularProgress, 
  Alert, 
  Divider,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  InputAdornment,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ru } from 'date-fns/locale';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useAuthStore } from '../../store/authStore';

/**
 * Form for creating and editing promotions
 */
const PromotionForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  // Проверяем валидность ID в URL и инициализируем currentId
  // Предварительная обработка paramId для исключения строки "undefined"
  const cleanParamId = paramId === 'undefined' || paramId === 'null' || !paramId ? null : paramId;
  const isNewPromotion = cleanParamId === 'new';
  const isValidId = Boolean(cleanParamId) && !isNewPromotion && cleanParamId !== 'undefined' && cleanParamId !== 'null';
  const [currentId, setCurrentId] = useState(isValidId ? cleanParamId : null);
  const isEditMode = Boolean(cleanParamId) && !isNewPromotion; // Если есть ID и не 'new', то это режим редактирования
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    imageUrl: '',
    startDate: null,
    endDate: null,
    isActive: true,
    priority: 0,
    buttonText: '',
    buttonLink: ''
  });
  
  // UI state
  const [loading, setLoading] = useState(isEditMode && isValidId); // Загрузка только если режим редактирования и ID валидный
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  
  // Обработчик ошибки авторизации
  const handleAuthError = useCallback(() => {
    console.error('Ошибка авторизации: Токен истек или недействителен');
    setError('Нет токена авторизации. Перенаправление на страницу входа...');
    // Перенаправляем на страницу входа через 2 секунды
    setTimeout(() => {
      window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
    }, 2000);
  }, []);
  
  // Проверка наличия токена
  const checkAuth = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!user || !token) {
      handleAuthError();
      return false;
    }
    return true;
  }, [user, handleAuthError]);
  
  // Логируем инициализацию
  console.log('Form initialization:', { 
    paramId, 
    paramIdType: typeof paramId, 
    currentId, 
    isEditMode, 
    isValidId,
    paramIdCheck: {
      isUndefined: paramId === undefined,
      isNull: paramId === null,
      isEmptyString: paramId === '',
      isStringUndefined: paramId === 'undefined',
      isStringNull: paramId === 'null',
      isStringNew: paramId === 'new'
    }
  });
  
  // Добавляем обработчик события authError
  useEffect(() => {
    window.addEventListener('authError', handleAuthError);
    
    // Проверяем авторизацию при загрузке компонента
    checkAuth();
    
    return () => {
      window.removeEventListener('authError', handleAuthError);
    };
  }, [handleAuthError, checkAuth]);

  // Загрузка данных акции
  const fetchPromotionData = useCallback(async () => {
    // Если это режим создания новой акции
    if (isNewPromotion) {
      console.log('Создание новой акции, пропускаем загрузку');
      setLoading(false);
      return;
    }
    
    // Проверяем валидность ID
    if (!isValidId || !currentId) {
      console.error('Некорректный ID акции:', currentId);
      setLoading(false);
      return;
    }
    
    // Проверяем авторизацию
    if (!checkAuth()) {
      setLoading(false);
      return;
    }
    
    console.log('Fetching promotion data for ID:', currentId);
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/promotions/${currentId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        timeout: 10000 // 10 секунд таймаут
      });
      
      console.log('Promotion data received:', response.data);
      
      const promotionData = response.data;
      setFormData({
        title: promotionData.title || '',
        description: promotionData.description || '',
        imageUrl: promotionData.imageUrl || '',
        startDate: promotionData.startDate ? new Date(promotionData.startDate) : null,
        endDate: promotionData.endDate ? new Date(promotionData.endDate) : null,
        isActive: promotionData.isActive !== undefined ? promotionData.isActive : true,
        priority: promotionData.priority || 0,
        buttonText: promotionData.buttonText || '',
        buttonLink: promotionData.buttonLink || ''
      });
      
      if (promotionData.imageUrl) {
        console.log('Setting image preview:', promotionData.imageUrl);
        setImagePreview(promotionData.imageUrl);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching promotion:', err);
      console.error('Error details:', err.response ? err.response.data : 'No response data');
      setError(`Не удалось загрузить данные акции: ${err.message}`);
    } finally {
      console.log('Finished loading promotion data, setting loading to false');
      setLoading(false);
    }
  }, [currentId, isEditMode, isValidId, user, checkAuth]);

  // Загрузка данных или редирект при некорректном ID
  useEffect(() => {
    console.log('Инициализация компонента:', { 
      paramId, 
      cleanParamId,
      isNewPromotion, 
      isValidId, 
      isEditMode, 
      currentId 
    });
    
    // Сценарий 1: Создание новой акции
    if (isNewPromotion) {
      console.log('Создание новой акции');
      setLoading(false);
      return;
    }
    
    // Сценарий 2: Отсутствует ID или некорректный ID
    if (!isValidId) {
      // Если параметр был некорректным (например, "undefined")
      if (paramId && paramId !== 'new') {
        console.log('Некорректный ID акции:', paramId);
        navigate('/admin/promotions', { 
          replace: true,
          state: { error: 'Некорректный ID акции' } 
        });
      } else if (!cleanParamId) {
        // Если ID отсутствует или был очищен до null
        console.log('Отсутствует ID, перенаправление на создание новой акции');
        navigate('/admin/promotions/new', { replace: true });
      }
      return;
    }
    
    // Сценарий 3: Режим редактирования с валидным ID
    if (isEditMode && isValidId && currentId) {
      console.log('Загрузка данных акции для редактирования:', currentId);
      fetchPromotionData();
    } else {
      setLoading(false);
    }
  }, [isEditMode, isValidId, currentId, paramId, navigate, fetchPromotionData]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Handle date changes
  const handleDateChange = (name, date) => {
    setFormData(prev => ({
      ...prev,
      [name]: date
    }));
  };
  
  // Handle image file selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImageFile(file);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };
  
  // Upload image to server
  const handleImageUpload = async (promotionId) => {
    // Проверяем, что есть файл для загрузки
    if (!imageFile) {
      console.log('No image file to upload');
      return;
    }
    
    // Проверяем наличие ID акции
    const targetId = promotionId || currentId;
    if (!targetId) {
      console.error('No promotion ID available for image upload');
      setError('Не удалось загрузить изображение: отсутствует ID акции');
      return;
    }
    
    // Проверяем авторизацию
    if (!checkAuth()) {
      return;
    }
    
    console.log(`Uploading image for promotion ID: ${targetId}`);
    
    try {
      setUploading(true);
      setUploadProgress(0);
      
      const imageFormData = new FormData();
      // Добавляем отладочное логирование
      console.log('Загрузка изображения для акции:', { 
        imageFile: imageFile ? imageFile.name : null, 
        targetId, 
        size: imageFile ? imageFile.size : 0 
      });
      
      // Добавляем файл с двумя разными именами полей для совместимости
      imageFormData.append('image', imageFile); // Для совместимости с текущим бэкендом
      imageFormData.append('imageFile', imageFile); // Для совместимости с альтернативным именем поля
      
      // Получаем токен из localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Отсутствует токен авторизации');
        return;
      }
      console.log('Отправляем запрос на загрузку изображения с увеличенным таймаутом');
      const response = await axios.post(
        `/api/promotions/${targetId}/image`, 
        imageFormData, 
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
            console.log('Прогресс загрузки:', percentCompleted + '%');
          },
          timeout: 120000 // 120 секунд таймаут для больших изображений
        }
      );
      console.log('Image upload response:', response.data);
      
      // Обновляем URL изображения в форме
      setFormData(prev => ({
        ...prev,
        imageUrl: response.data.imageUrl
      }));
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Error uploading image:', err);
      
      // Подробный анализ ошибки
      if (err.code === 'ECONNABORTED') {
        console.log('Ошибка таймаута при загрузке изображения, но изображение могло быть загружено на сервер');
        
        // Пробуем получить текущие данные акции, чтобы проверить изображение
        setTimeout(async () => {
          try {
            const refreshResponse = await axios.get(`/api/promotions/${targetId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            if (refreshResponse.data && refreshResponse.data.imageUrl) {
              console.log('Изображение было успешно загружено, несмотря на ошибку таймаута:', refreshResponse.data.imageUrl);
              
              // Обновляем URL изображения в форме
              setFormData(prev => ({
                ...prev,
                imageUrl: refreshResponse.data.imageUrl
              }));
              
              setSuccess(true);
              setTimeout(() => setSuccess(false), 3000);
              return;
            }
          } catch (refreshErr) {
            console.error('Ошибка при попытке получить обновленные данные акции:', refreshErr);
          }
          
          setError(`Не удалось загрузить изображение: Таймаут запроса. Попробуйте обновить страницу.`);
        }, 2000);
      } else {
        setError(`Не удалось загрузить изображение: ${err.message}`);
      }
    } finally {
      setUploading(false);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Проверяем авторизацию
    if (!checkAuth()) {
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      const promotionData = {
        ...formData,
        // Преобразуем даты в ISO формат для отправки на сервер
        startDate: formData.startDate ? formData.startDate.toISOString() : null,
        endDate: formData.endDate ? formData.endDate.toISOString() : null
      };
      
      console.log('Submitting promotion data:', promotionData);
      let response;
      
      // Получаем токен из localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Отсутствует токен авторизации. Перенаправление на страницу входа...');
        setTimeout(() => navigate('/admin/login'), 2000);
        return;
      }
      
      // Устанавливаем увеличенный таймаут для загрузки изображений
      // Загрузка на VPS может занимать больше времени
      if (isEditMode && currentId) {
        // Обновление существующей акции
        response = await axios.put(
          `/api/promotions/${currentId}`, 
          promotionData, 
          {
            headers: {
              Authorization: `Bearer ${token}`
            },
            timeout: 60000 // 60 секунд таймаут для загрузки изображения
          }
        );
        
        console.log('Promotion updated:', response.data);
      } else {
        // Создание новой акции
        response = await axios.post(
          '/api/promotions', 
          promotionData, 
          {
            headers: {
              Authorization: `Bearer ${token}`
            },
            timeout: 10000 // 10 секунд таймаут
          }
        );
        
        console.log('Promotion created:', response.data);
        
        // Устанавливаем ID новой акции
        const newPromotionId = response.data._id;
        setCurrentId(newPromotionId);
        
        // Если есть файл изображения, загружаем его после создания акции
        if (imageFile) {
          console.log('Waiting before uploading image for new promotion...');
          // Небольшая задержка перед загрузкой изображения, чтобы быть уверенным, что акция создана
          setTimeout(() => {
            handleImageUpload(newPromotionId);
          }, 500);
        }
      }
      
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      
      // Если это была новая акция, перенаправляем на страницу редактирования с новым ID
      if (!isEditMode) {
        navigate(`/admin/promotions/edit/${response.data._id}`, { replace: true });
      }
    } catch (err) {
      console.error('Error saving promotion:', err);
      console.error('Error details:', err.response ? err.response.data : 'No response data');
      setError(`Не удалось сохранить акцию: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/admin/promotions')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          {isEditMode ? 'Редактирование акции' : 'Создание новой акции'}
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {isEditMode ? 'Акция успешно обновлена!' : 'Акция успешно создана!'}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <form onSubmit={handleSubmit}>
              <TextField
                name="title"
                label="Название акции"
                value={formData.title}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
                variant="outlined"
              />
              
              <TextField
                name="description"
                label="Описание акции"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
                variant="outlined"
                multiline
                rows={4}
              />
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
                    <DatePicker
                      label="Дата начала"
                      value={formData.startDate}
                      onChange={(date) => handleDateChange('startDate', date)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          margin="normal"
                          variant="outlined"
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
                    <DatePicker
                      label="Дата окончания"
                      value={formData.endDate}
                      onChange={(date) => handleDateChange('endDate', date)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          margin="normal"
                          variant="outlined"
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="priority"
                    label="Приоритет"
                    type="number"
                    value={formData.priority}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography variant="body2" color="textSecondary">
                            0-100
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={handleChange}
                        name="isActive"
                        color="primary"
                      />
                    }
                    label="Активна"
                    sx={{ mt: 2 }}
                  />
                </Grid>
              </Grid>
              
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" sx={{ mb: 2 }}>
                Настройки кнопки (опционально)
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="buttonText"
                    label="Текст кнопки"
                    value={formData.buttonText}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    placeholder="Например: Забронировать"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="buttonLink"
                    label="Ссылка кнопки"
                    value={formData.buttonLink}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    placeholder="Например: /booking"
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/promotions')}
                  sx={{ mr: 2 }}
                  disabled={saving}
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={saving}
                >
                  {saving ? 'Сохранение...' : 'Сохранить'}
                </Button>
              </Box>
            </form>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Изображение акции
            </Typography>
            
            <Card sx={{ mb: 3 }}>
              {imagePreview ? (
                <CardMedia
                  component="img"
                  height="200"
                  image={imagePreview}
                  alt="Preview"
                  sx={{ objectFit: 'cover' }}
                />
              ) : (
                <Box
                  sx={{
                    height: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme.palette.grey[100]
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    Нет изображения
                  </Typography>
                </Box>
              )}
              <CardContent>
                <Typography variant="body2" color="textSecondary">
                  Рекомендуемый размер: 1200x600px
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  sx={{ mr: 1 }}
                >
                  Выбрать файл
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleImageChange}
                  />
                </Button>
                {imageFile && isEditMode && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleImageUpload()}
                    disabled={uploading}
                    startIcon={uploading ? <CircularProgress size={20} /> : null}
                  >
                    {uploading ? `${uploadProgress}%` : 'Загрузить'}
                  </Button>
                )}
                {imagePreview && (
                  <IconButton 
                    color="error" 
                    onClick={() => {
                      setImagePreview('');
                      setImageFile(null);
                      setFormData(prev => ({ ...prev, imageUrl: '' }));
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </CardActions>
            </Card>
            
            <Alert severity="info">
              {isEditMode 
                ? 'Для обновления изображения выберите новый файл и нажмите "Загрузить"'
                : 'Изображение будет загружено автоматически при сохранении акции'}
            </Alert>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PromotionForm;
